import { useState } from 'react';

export interface DateRangeValue {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
  preset?: 'today' | 'yesterday' | 'event' | 'custom';
}

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  /** Event-to-date defaults */
  eventStartsOn: string;
  eventTimezone: string;
}

/**
 * Format a Date as YYYY-MM-DD in the given IANA timezone. Uses the 'en-CA'
 * locale because it natively renders YYYY-MM-DD, sidestepping per-locale
 * order/separator drift.
 */
function isoDateInTz(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function todayInTz(timeZone: string): string {
  return isoDateInTz(new Date(), timeZone);
}

function yesterdayInTz(timeZone: string): string {
  // Subtract 24h before formatting in the target zone. Around DST transitions
  // this can be off by an hour; that's harmless for "what is yesterday's date"
  // — we just need the calendar day, and 24h-prior is always on the previous
  // day in any zone.
  return isoDateInTz(new Date(Date.now() - 24 * 60 * 60 * 1000), timeZone);
}

export function rangeForPreset(
  preset: DateRangeValue['preset'],
  args: { eventStartsOn: string; eventTimezone: string },
): DateRangeValue {
  if (preset === 'today') {
    const t = todayInTz(args.eventTimezone);
    return { from: t, to: t, preset: 'today' };
  }
  if (preset === 'yesterday') {
    const y = yesterdayInTz(args.eventTimezone);
    return { from: y, to: y, preset: 'yesterday' };
  }
  if (preset === 'event') {
    return {
      from: args.eventStartsOn,
      to: todayInTz(args.eventTimezone),
      preset: 'event',
    };
  }
  return {
    from: args.eventStartsOn,
    to: todayInTz(args.eventTimezone),
    preset: 'custom',
  };
}

export function DateRangePicker({
  value,
  onChange,
  eventStartsOn,
  eventTimezone,
}: DateRangePickerProps) {
  const [showCustom, setShowCustom] = useState<boolean>(value.preset === 'custom');

  const onPreset = (preset: 'today' | 'yesterday' | 'event' | 'custom') => {
    if (preset === 'custom') {
      setShowCustom(true);
      onChange({ ...value, preset });
      return;
    }
    setShowCustom(false);
    onChange(rangeForPreset(preset, { eventStartsOn, eventTimezone }));
  };

  return (
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      <button
        type="button"
        className={`filter-chip${value.preset === 'today' ? '' : ''}`}
        data-active={value.preset === 'today'}
        onClick={() => onPreset('today')}
      >
        Today
      </button>
      <button
        type="button"
        className="filter-chip"
        data-active={value.preset === 'yesterday'}
        onClick={() => onPreset('yesterday')}
      >
        Yesterday
      </button>
      <button
        type="button"
        className="filter-chip"
        data-active={value.preset === 'event'}
        onClick={() => onPreset('event')}
      >
        Event-to-date
      </button>
      <button
        type="button"
        className="filter-chip"
        data-active={value.preset === 'custom'}
        onClick={() => onPreset('custom')}
      >
        Custom
      </button>
      {showCustom && (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 4 }}>
          <input
            type="date"
            value={value.from}
            onChange={(e) => onChange({ ...value, from: e.target.value, preset: 'custom' })}
            aria-label="From"
          />
          <span style={{ color: 'var(--fg-3)' }}>→</span>
          <input
            type="date"
            value={value.to}
            onChange={(e) => onChange({ ...value, to: e.target.value, preset: 'custom' })}
            aria-label="To"
          />
        </span>
      )}
    </div>
  );
}
